import React, { useMemo, useState } from "react";
import { Users, Wifi, WifiOff, Signal, ChevronDown, ChevronUp } from "lucide-react";

/**
 * ViewerStatsHud — host-only live HUD showing per-viewer connection health.
 *
 * Renders as a compact pill in the top-right of the broadcast preview while
 * the stream is live. Click to expand into a list of every viewer with a
 * colored dot per their ICE state:
 *   🟢 connected / completed   — healthy
 *   🟡 checking / new          — negotiating
 *   🔴 failed / disconnected   — needs attention
 *   ⚪ closed / unknown        — stale
 *
 * The host uses this to spot connection trouble *before* a viewer complains.
 * Especially useful for call-in shows where the host needs to know a guest's
 * feed is unstable before bringing them on-air.
 */
export default function ViewerStatsHud({ viewers = [], viewerStates = {} }) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => viewers.map((v) => ({
    ...v,
    state: viewerStates[v.user_id] || "new",
  })), [viewers, viewerStates]);

  const counts = useMemo(() => {
    let green = 0, yellow = 0, red = 0;
    for (const r of rows) {
      if (r.state === "connected" || r.state === "completed") green++;
      else if (r.state === "checking" || r.state === "new") yellow++;
      else if (r.state === "failed" || r.state === "disconnected") red++;
    }
    return { green, yellow, red };
  }, [rows]);

  if (rows.length === 0) return null;

  return (
    <div
      data-testid="viewer-stats-hud"
      className="absolute top-3 right-3 z-10 bg-black/75 backdrop-blur-md rounded-lg border border-white/10 text-white text-xs font-bold shadow-[0_6px_24px_-8px_rgba(0,0,0,0.8)]"
    >
      <button
        data-testid="viewer-stats-hud-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-white/5 rounded-lg w-full"
      >
        <Users className="w-3.5 h-3.5 text-white/80" />
        <span className="tabular-nums">{rows.length}</span>
        <span className="flex items-center gap-1.5">
          {counts.green > 0 && <span className="flex items-center gap-0.5 text-emerald-300"><Dot color="bg-emerald-400" /> {counts.green}</span>}
          {counts.yellow > 0 && <span className="flex items-center gap-0.5 text-amber-300"><Dot color="bg-amber-400" /> {counts.yellow}</span>}
          {counts.red > 0 && <span className="flex items-center gap-0.5 text-red-300"><Dot color="bg-red-400 animate-pulse" /> {counts.red}</span>}
        </span>
        {open ? <ChevronUp className="w-3 h-3 opacity-60" /> : <ChevronDown className="w-3 h-3 opacity-60" />}
      </button>

      {open && (
        <div className="border-t border-white/10 max-h-64 overflow-y-auto" data-testid="viewer-stats-hud-list">
          {rows.map((r) => (
            <div
              key={r.user_id}
              data-testid={`viewer-stats-row-${r.user_id}`}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-white/5 border-b border-white/5 last:border-b-0"
            >
              <StateIcon state={r.state} />
              <span className="truncate flex-1 max-w-[140px] font-medium">{r.name || "Viewer"}</span>
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-black">{labelFor(r.state)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Dot({ color }) {
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color}`} />;
}

function StateIcon({ state }) {
  if (state === "connected" || state === "completed") return <Wifi className="w-3.5 h-3.5 text-emerald-400 shrink-0" />;
  if (state === "checking" || state === "new") return <Signal className="w-3.5 h-3.5 text-amber-400 shrink-0 animate-pulse" />;
  if (state === "failed" || state === "disconnected") return <WifiOff className="w-3.5 h-3.5 text-red-400 shrink-0 animate-pulse" />;
  return <Wifi className="w-3.5 h-3.5 text-white/30 shrink-0" />;
}

function labelFor(state) {
  if (state === "connected" || state === "completed") return "good";
  if (state === "checking") return "connecting";
  if (state === "new") return "joining";
  if (state === "failed") return "failed";
  if (state === "disconnected") return "drop";
  if (state === "closed") return "closed";
  return state;
}
